import { isEmpty } from 'lodash';
import React from 'react';
import { compose } from 'redux';
import { withProps } from 'recompose';
import { withRouter } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';

import * as ENUM from '@dpdgroupuk/redback-enums';
import { withPageControl } from '@dpdgroupuk/fmx-ui/components/Page';
import { IMAGES } from '@dpdgroupuk/fmx-ui/themes/constants';
import Wizard, { Step } from '@dpdgroupuk/fmx-ui/components/Wizard';
import { withModal } from '@dpdgroupuk/fmx-ui/components/Dialog';

import type { AuthorizedParcel } from '../../../../types/Parcel';
import SelectNeighbourStep from './SelectNeighbourStep';
import SelectDayStep from './SelectDayStep';
import CompleteStep from './CompleteStep';
import {
  DTN_DELIVERY_OPTION,
  trackable,
} from '../../../../constants/analytics';
import {
  backToDpdSiteHeader,
  backToGreenSiteHeader,
  doSomethingDifferentHeader,
  doSomethingDifferentSpeedDeal,
  getDpdAppHeader,
  goBackToDeliveryDetailsSpeedDeal,
  trackAnotherParcelHeader,
  whatHappensNextHeader,
  whatHappensNextSpeedDeal,
} from '../../../controlActions';
import * as MESSAGES from '../../../../constants/message';
import DeliveryPreferencesStep from './DeliveryPreferencesStep';
import withParcel from '../../../../HOCs/withParcel';
import { fetchActionsDatesByCode } from '../../actions';
import { DTN_STEPS } from '../../steps';
import { withDeliveryUpdate } from '../../hocs';
import { changeDeliveryToNeighbourAndDate } from './actions';
import { isCreateProfilePossible } from '../../../../models/parcel';
import GetContactDetailsStep from '../../components/GetContactDetailsStep';
import withFetch from '../../../../HOCs/withFetch';
import GetContactDetailsNoEmailStep from '../../components/GetContactDetailsNoEmailStep';
import EnterCodeStep from '../../components/EnterCodeStep';

const useStyles = makeStyles(({ typography, breakpoints }) => ({
  selectNeighbourStep: {
    background: 'transparent',
  },
  completeStep: {
    paddingLeft: typography.pxToRem(120),
    paddingRight: typography.pxToRem(120),
    [breakpoints.down('xs')]: {
      paddingLeft: typography.pxToRem(15),
      paddingRight: typography.pxToRem(15),
    },
  },
}));

type Props = {
  parcel: AuthorizedParcel,
  dates: Array<string>,
  updateDelivery: Function,
  onSubmitSuccess: Function,
  onSubmitFailure: Function,
};

const DeliverToNeighbourComponent = ({
  parcel,
  dates,
  updateDelivery,
  onSubmitSuccess,
  onSubmitFailure,
}: Props) => {
  const styles = useStyles();

  return (
    <Wizard
      initialStep={
        isEmpty(dates) ? DTN_STEPS.SELECT_NEIGHBOUR : DTN_STEPS.SELECT_DAY
      }
      onSubmit={updateDelivery}
      onSubmitSuccess={onSubmitSuccess}
      onSubmitFailure={onSubmitFailure}
    >
      <Step
        stepId={DTN_STEPS.SELECT_DAY}
        component={SelectDayStep}
        dates={dates}
      />
      <Step
        stepId={DTN_STEPS.SELECT_NEIGHBOUR}
        component={SelectNeighbourStep}
        parcel={parcel}
        isCreateProfilePossible={isCreateProfilePossible(parcel)}
        wrapperClassName={styles.selectNeighbourStep}
      />

      {/* SMS Authentication */}

      <Step
        stepId={DTN_STEPS.GET_CONTACTS}
        component={GetContactDetailsNoEmailStep}
        parcel={parcel}
        nextStep={DTN_STEPS.ENTER_CODE}
      />
      <Step
        stepId={DTN_STEPS.ENTER_CODE}
        component={EnterCodeStep}
        parcel={parcel}
      />

      {/* / SMS Authentication / */}

      <Step
        stepId={DTN_STEPS.GET_CONTACTS}
        component={GetContactDetailsStep}
        parcel={parcel}
      />
      <Step
        stepId={DTN_STEPS.DELIVERY_PREFERENCES}
        component={DeliveryPreferencesStep}
        updateDelivery={updateDelivery}
        isCreateProfilePossible={isCreateProfilePossible(parcel)}
        parcel={parcel}
      />
      <Step
        stepId={DTN_STEPS.COMPLETE}
        component={CompleteStep}
        parcel={parcel}
        actionCode={ENUM.PARCEL_ACTION_CODE.DTN}
        wrapperClassName={styles.completeStep}
      />
    </Wizard>
  );
};

const createHeaderControlActions = props => [
  doSomethingDifferentHeader(props),
  trackAnotherParcelHeader(props),
  whatHappensNextHeader(props),
  backToDpdSiteHeader(props),
  backToGreenSiteHeader(props),
  getDpdAppHeader(props),
];

const createSpeedDealControlActions = props => [
  doSomethingDifferentSpeedDeal(props),
  goBackToDeliveryDetailsSpeedDeal(props),
  whatHappensNextSpeedDeal(props),
];

const DeliverToNeighbour = compose(
  withRouter,
  withParcel(),
  withModal,
  withFetch(({ match }) =>
    fetchActionsDatesByCode(match.params.parcelId, ENUM.PARCEL_ACTION_CODE.DTN)
  ),
  withProps(props => ({
    dates: props.fetchedData,
    onSubmitSuccess: ({ goToStep }) => goToStep(DTN_STEPS.COMPLETE),
  })),
  withDeliveryUpdate({
    actionCode: ENUM.PARCEL_ACTION_CODE.DTN,
    changeDeliveryAction: changeDeliveryToNeighbourAndDate,
    trackable: DTN_DELIVERY_OPTION.ON_SUBMIT,
  }),
  withPageControl({
    title: MESSAGES.DELIVER_TO_NEIGHBOUR,
    backgroundImage: IMAGES.MAP,
    trackable: trackable(DTN_DELIVERY_OPTION),
    speedDealActions: createSpeedDealControlActions,
    headerMenuActions: createHeaderControlActions,
  })
)(DeliverToNeighbourComponent);

export default DeliverToNeighbour;
